import React, { useEffect, useMemo, useState } from 'react'
import { Stack } from '@mui/material'
import EcoFriendlyFeaturesDescription from './ecoFriendlyFeatures/EcoFriendlyFeaturesDescription'
import EcoFriendlyFeaturesCharacteristics from './ecoFriendlyFeatures/EcoFriendlyFeaturesCharacteristics'
import EcoFriendlyFeatures from './ecoFriendlyFeatures/EcoFriendlyFeatures'
import useTitle from '../../../../utils/customHook/useTitle'
import useActions from '../../../../utils/customHook/useActions'
import i18n from '../../../../simple-react-i18n'
import {
    PATH_REFERENCIAL,
    PATH_REFERENCIAL_TAXON,
    PATH_REFERENCIAL_TAXON_LINK,
} from '../../../../home/constants/RouteConstants'
import { useParams } from 'react-router'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import TaxonAction from '../actions/TaxonAction'
import { getUser } from '../../../../utils/SettingUtils'
import { isNil } from 'lodash'
import ReferencialAction from '../../../action/ReferencialAction'

const traitKey = t => `${t.traitCode}_${t.modalityCode}`
const sameTrait = (a, b) => traitKey(a) === traitKey(b)

const TaxonEcoFriendlyFeatures = () => {
    const {
        taxonProps,
        sandreCodes,
        ecobioTaxonTraits,
    } = useSelector(store => ({
        taxonProps: store.TaxonReducer.taxon,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        ecobioTaxonTraits: store.TaxonReducer.ecobioTaxonTraits,
    }), shallowEqual)

    const { code } = useParams()
    const dispatch = useDispatch()

    const [taxon, setTaxon] = useState({})
    const [taxonTraits, setTaxonTraits] = useState([])
    const [isEditMode, setIsEditMode] = useState(false)

    const isConsultant = useMemo(() => getUser().consultant === '1', [])

    useEffect(() => {
        if (isNil(taxonProps?.code)) {
            dispatch(TaxonAction.fetchTaxon(code))
        }
        dispatch(TaxonAction.fetchEcobioTaxonTraits(code))
    }, [code, dispatch, taxonProps?.code])

    useEffect(() => {
        if (!isNil(taxonProps?.code)) {
            setTaxon(taxonProps)
        }
        if (!sandreCodes.length) {
            dispatch(ReferencialAction.fetchSandreCodes())
        }
    }, [taxonProps])

    useEffect(() => {
        setTaxonTraits(ecobioTaxonTraits)
    }, [ecobioTaxonTraits])

    const onChange = changes => setTaxon(prev => ({ ...prev, ...changes }))

    const onAddTaxonTrait = association => setTaxonTraits(prev => (prev.some(t => sameTrait(t, association)) ? prev : [...prev, association]))
    const onDeleteTaxonTrait = association => setTaxonTraits(prev => prev.filter(t => !sameTrait(t, association)))

    const saveTaxonTraits = () => {
        const added = taxonTraits.filter(t => !ecobioTaxonTraits.some(o => sameTrait(o, t)))
        const removed = ecobioTaxonTraits.filter(o => !taxonTraits.some(t => sameTrait(t, o)))
        added.forEach(a => dispatch(TaxonAction.createEcobioTaxonTrait(a)))
        removed.forEach(r => dispatch(TaxonAction.deleteEcobioTaxonTrait(r)))
    }

    useActions(() => {
        if (isConsultant) {
            return {}
        }
        if (isEditMode) {
            return {
                save: () => {
                    dispatch(TaxonAction.updateTaxon(taxon))
                    saveTaxonTraits()
                    setIsEditMode(false)
                },
                cancel: () => {
                    setTaxon(taxonProps)
                    setTaxonTraits(ecobioTaxonTraits)
                    setIsEditMode(false)
                },
            }
        }
        return {
            edit: () => setIsEditMode(true),
        }
    }, [isConsultant, isEditMode, taxon, taxonProps, taxonTraits, ecobioTaxonTraits])

    useTitle(() => [
        {
            title: i18n.referencials,
            href: PATH_REFERENCIAL,
        },
        {
            title: i18n.taxons,
            href: PATH_REFERENCIAL_TAXON,
        },
        {
            title: code + (taxonProps.latinName ? ` - ${taxonProps.latinName}` : ''),
            href: PATH_REFERENCIAL_TAXON_LINK + code,
        },
        {
            title: i18n.ecoFriendlyFeatures,
            href: `${PATH_REFERENCIAL_TAXON}/${code}/ecoFriendlyFeatures`,
        },
    ], [code, taxonProps])

    return (
        <Stack spacing={1} width={'100%'}>
            <EcoFriendlyFeaturesDescription taxon={taxon} onChange={onChange} disabled={!isEditMode} />
            <EcoFriendlyFeaturesCharacteristics taxon={taxon} onChange={onChange} disabled={!isEditMode} />
            <EcoFriendlyFeatures
                taxon={taxon}
                taxonTraits={taxonTraits}
                onAdd={onAddTaxonTrait}
                onDelete={onDeleteTaxonTrait}
                disabled={!isEditMode}
            />
        </Stack>
    )
}

export default TaxonEcoFriendlyFeatures